import { useEffect, useState } from "react";
import { literals } from "@/constants/literals";
import Pagination from "@/components/Pagination/Pagination";
import AsidePhotoDetail from "./components/AsidePhotoDetail";
import type { OrderItem } from "@/types/types.d";
import { cn } from "@/lib/styles.utils";
import { formatDateDDMmmYYYY } from "@/lib/dates";
import OptionsTableChecks from "./components/OptionsTableChecks";
import { Icon } from "@/components/icons/Icon";
import FilterSection from "./components/FilterSection";
import "../../styles/InspectionsTable.css";

const RequestIcon = ({ row }: { row: OrderItem }) => {
  if (row.hasDeletionRequest) {
    return (
      <td className="p-3 text-red-600">
        <Icon name="remove" className="size-4 opacity-60" />
      </td>
    );
  }
  if (row.editionRequest && row.editionRequest.length > 0) {
    return (
      <td className="p-3 text-red-600">
        <Icon name="pencil" className="size-4 opacity-60" />
      </td>
    );
  }
  return <td className="p-3"></td>;
};

interface PaginationRsults {
  lastPage: number;
  page: number;
  changePage: (newPage: number) => void;
  limit: string;
  setLimit: (newPage: string) => void;
}

const PhotosResults = ({
  filters,
  handleFiltersChange,
  data,
  pagination,
  refetch,
}: {
  filters: any;
  handleFiltersChange: (new_filters: any) => void;
  data: OrderItem[];
  pagination: PaginationRsults;
  refetch: () => Promise<void>;
}) => {
  const { lastPage, page, changePage, limit, setLimit } = pagination;
  const [openPhotoDetail, setOpenPhotoDetail] = useState(false);
  const [photoDetail, setPhotoDetail] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const handleOnCloseAside = () => {
    setOpenPhotoDetail(false);
    setPhotoDetail(null);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedIds([]);
    handleOnCloseAside();
  }, [data]);

  return (
    <div className="w-full">
      <div>
        <FilterSection
          filters={filters}
          handleFiltersChange={handleFiltersChange}
        />
      </div>
      {/* Table */}
      {data.length > 0 ? (
        <div className="mt-4 border border-l border-ib-gray-lightest rounded-[8px] bg-white">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm">
                <th className="central-header border-b border-b-ib-gray-lightest">
                  <input
                    type="checkbox"
                    checked={
                      data.length > 0 && selectedIds.length === data.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(data.map((row) => row.imageId));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </th>
                <th className="central-header border-b border-b-ib-gray-lightest w-10" />
                <th className="central-header border-b border-b-ib-gray-lightest">
                  {literals.date}
                </th>
                <th className="central-header border-b border-b-ib-gray-lightest">
                  {literals.workstation}
                </th>
                <th className="central-header border-b border-b-ib-gray-lightest">
                  {literals.step}
                </th>
                <th className="central-header border-b border-b-ib-gray-lightest">
                  {literals.description}
                </th>
                <th className="central-header border-b border-b-ib-gray-lightest">
                  {literals.visibleClient}
                </th>
                <th className="central-header border-b border-b-ib-gray-lightest">
                  {literals.partIn}
                </th>
                <th className="central-header border-b border-b-ib-gray-lightest">
                  {literals.serialIn}
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedIds.length > 0 && (
                <OptionsTableChecks
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                />
              )}

              {data.map((row, idx) => (
                <tr
                  key={`${row.id}-${idx}`}
                  className={cn(
                    "h-16 border-b border-b-ib-gray-lightest text-sm cursor-pointer",
                    idx === data.length - 1 && "last-row",
                    photoDetail?.imageId === row.imageId && "font-ib-bold",
                    idx % 2 === 0 ? "table-even-row" : "table-odd-row",
                  )}
                  onClick={() => {
                    setPhotoDetail(row);
                    setOpenPhotoDetail(true);
                  }}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.imageId)}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedIds((prev) =>
                          e.target.checked
                            ? [...prev, row.imageId]
                            : prev.filter((id) => id !== row.imageId),
                        );
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <RequestIcon row={row} />
                  <td className="p-3">{formatDateDDMmmYYYY(row.createdAt)}</td>
                  <td className="p-3 font-semibold">{row.area}</td>
                  <td className="p-3">{row.step}</td>

                  <td className="p-3 truncate max-w-[400px]">
                    {row.description ?? "-"}
                  </td>
                  <td className="p-3">{row.visible ? "Sí" : "No"}</td>
                  <td className="p-3">{row.orderIn ?? "-"}</td>
                  <td className="p-3">{row.serialIn ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {openPhotoDetail && photoDetail && (
        <AsidePhotoDetail
          key={"photo-detail-" + photoDetail.imageId}
          handleOnCloseAside={handleOnCloseAside}
          photoDetail={photoDetail}
          refetch={refetch}
        />
      )}
      {lastPage > 1 ? (
        <div className="mt-4">
          <Pagination
            lastPage={lastPage}
            currentPage={page + 1} // backend is 0-based
            changePage={changePage}
            limit={limit}
            setLimit={setLimit}
          />
        </div>
      ) : null}
    </div>
  );
};

export default PhotosResults;
